import { Controller } from '@hotwired/stimulus'
import { isEqual } from 'lodash'
import renderTemplate from '../../api/helpers/templating'
import { Chart, UserDataValue } from '../../api/models/types'
import {
  getComponentContext,
  lifecycleTrigger,
  connectUserData,
} from '../../helpers/controllerHelpers/utils'

declare global {
  interface Window {
    Chart: any
  }
}

type ChartConfig = {
  type: string
  data: {
    labels: any
    datasets: any
  }
  options?: any
}

export default class extends Controller {
  component: Chart
  disconnectUserData: (() => void) | undefined
  declare userDataValue: { [key: string]: unknown }
  declare lastDataLabelsValue: any
  declare lastDataSetsValue: any
  chartInstance: any

  connect() {
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    const chartScriptUrl = 'https://cdn.jsdelivr.net/npm/chart.js'
    const chartScript = document.querySelector(`script[src="${chartScriptUrl}"]`) as
      | HTMLScriptElement
      | undefined
    const chartScriptFound = !!chartScript
    const { component } = getComponentContext(this)
    this.component = component as Chart
    if (!chartScriptFound) {
      const script = document.createElement('script')
      script.src = chartScriptUrl
      script.onload = () => {
        this.setUpChart()
        const event = new Event('chart_script_loaded')
        script.dispatchEvent(event)
      }
      document.body.appendChild(script)
    } else if (window.Chart) {
      this.setUpChart()
    } else {
      chartScript.addEventListener(
        'chart_script_loaded',
        () => {
          this.setUpChart()
        },
        { once: true }
      )
    }
    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (this.disconnectUserData) this.disconnectUserData()
  }

  userDataValueChanged(userData: UserDataValue) {
    if (userData === undefined || this.lastDataLabelsValue === undefined) return

    // Deep comparison of objects
    const labelsEqual = isEqual(this.lastDataLabelsValue, this.templatedLabels())
    const datasetsEqual = isEqual(this.lastDataSetsValue, this.templatedDataSets())

    if (!labelsEqual || !datasetsEqual) {
      // If it has changed I update the chart
      this.setUpChart()
    }
  }

  setUpChart() {
    // Check if there's an existing chart instance
    if (this.chartInstance) {
      // Destroy the existing chart instance
      this.chartInstance.destroy()
    }

    // Templating data.labels and storing last value to compare with new value
    const dataLabels = this.templatedLabels()
    this.lastDataLabelsValue = dataLabels

    // Templating data.datasets.data and storing last value to compare with new value
    const dataSets = this.templatedDataSets()
    this.lastDataSetsValue = dataSets

    const options = this.component.options

    const config: ChartConfig = {
      type: this.component.chart_type || 'line',
      data: {
        labels: dataLabels,
        datasets: dataSets?.map((dataSet) => {
          return {
            label: dataSet.label,
            data: dataSet.data,
            backgroundColor: dataSet.background_color,
            fill: dataSet.fill,
            tension: dataSet.line_tension,
            borderColor: dataSet.border_color,
            borderWidth: dataSet.border_width,
          }
        }),
      },
    }

    if (options) config.options = options

    this.initChart(config)
  }

  initChart(config) {
    this.chartInstance = new window.Chart.Chart(this.element, config)
    // Adding the chart instance to the DOM element so it can be accessed from flow custom code
    ;(this.element as any).chart_instance = this.chartInstance
  }

  templatedLabels() {
    let result
    if (this.component.data?.labels?.includes('{{')) {
      result = (
        renderTemplate(this.component.data?.labels as string, this.userDataValue || {}) || ''
      ).split(',')
      return result
    } else {
      return this.component.data?.labels || []
    }
  }

  templatedDataSets() {
    //* first we need to template the whole dataSets property value and then we template the data and label properties of each dataSet

    let dataSetsJSON
    //* if the dataSets property is a string, it meand it needs to be templated
    if (typeof this.component.data?.datasets === 'string') {
      const templatedDataSets =
        renderTemplate(this.component.data?.datasets, this.userDataValue || {}) || ''
      try {
        dataSetsJSON = Array.isArray(templatedDataSets)
          ? templatedDataSets
          : JSON.parse(templatedDataSets)
      } catch (error) {
        dataSetsJSON = templatedDataSets
      }
    } else {
      dataSetsJSON = this.component.data?.datasets
    }

    //if the dataSets property is an array of objects, we need to template each object "data" and "label" properties
    const result = !dataSetsJSON
      ? []
      : dataSetsJSON?.map((dataSet) => {
          return {
            ...dataSet,
            data:
              typeof dataSet.data === 'string'
                ? (renderTemplate(dataSet.data, this.userDataValue || {}) || '').split(',')
                : dataSet.data,
            label:
              typeof dataSet.label === 'string'
                ? (renderTemplate(dataSet.label, this.userDataValue || {}) || '').split(',')
                : dataSet.label,
          }
        })
    return result
  }
}
